import type { GetAssignmentQuery } from '../gql/graphql'

type AssignmentResult = NonNullable<GetAssignmentQuery['assignment']>
type RouteCrewMember = NonNullable<
  AssignmentResult['route']
>['assignments'][number]

/** A Team row: a route crew member, or the assignment's own operator (no phones selected) */
export type TeamAssignment = Pick<RouteCrewMember, 'id' | 'roleDetails'> & {
  operator: AssignmentResult['operator'] & {
    phones?: RouteCrewMember['operator']['phones']
  }
}

type TeamSource = Pick<
  AssignmentResult,
  'id' | 'active' | 'roleDetails' | 'operator'
> & {
  route?: { assignments: RouteCrewMember[] } | null
}

const byRoleSort = (a: TeamAssignment, b: TeamAssignment) =>
  a.roleDetails.sort - b.roleDetails.sort

const toOwnTeamEntry = (assignment: TeamSource): TeamAssignment => ({
  id: assignment.id,
  roleDetails: assignment.roleDetails,
  operator: assignment.operator,
})

/**
 * Team members to list on the assignment detail (v1 rule).
 * An active assignment lists the route's crew by role. An inactive one
 * (cancelled, unassigned) lists only its own operator: the route's crew is
 * whoever replaced them, not this operator's team. Without a route crew the
 * operator is the team. The API already limits `route.assignments` to active
 * assignments (Route.assignments resolver), so the crew is not re-filtered.
 */
export const teamAssignmentsFor = (
  assignment: TeamSource | null | undefined,
): TeamAssignment[] => {
  if (!assignment) return []
  if (!assignment.active) return [toOwnTeamEntry(assignment)]

  const routeCrew = assignment.route?.assignments ?? []
  if (routeCrew.length === 0) return [toOwnTeamEntry(assignment)]

  return [...routeCrew].sort(byRoleSort)
}
